import { useEffect, useState } from 'react'
import AnimationLayout from './components/AnimationLayout'

interface Badge {
  name: string
  emoji: string
  description: string
  points_awarded: number
}

interface Props {
  animation: { id: number; name: string; description: string }
  badge: Badge
  all_animations: { id: number; name: string }[]
}

export default function Animation38({ animation, badge, all_animations }: Props) {
  const [crystalPhase, setCrystalPhase] = useState(0)
  const [showBadge, setShowBadge] = useState(false)

  useEffect(() => {
    setTimeout(() => setCrystalPhase(1), 200)
    setTimeout(() => setCrystalPhase(2), 600)
    setTimeout(() => setCrystalPhase(3), 1000)
    setTimeout(() => setCrystalPhase(4), 1400)
    setTimeout(() => {
      setCrystalPhase(5)
      setShowBadge(true)
    }, 1800)
  }, [])

  const crystals = [
    { rotate: 0, delay: 0, scale: 1 },
    { rotate: 60, delay: 0.1, scale: 0.9 },
    { rotate: 120, delay: 0.2, scale: 0.95 },
    { rotate: 180, delay: 0.3, scale: 0.85 },
    { rotate: 240, delay: 0.4, scale: 0.9 },
    { rotate: 300, delay: 0.5, scale: 0.88 }
  ]

  return (
    <AnimationLayout animation={animation} allAnimations={all_animations}>
      <style>{`
        @keyframes crystalForm {
          0% {
            transform: rotate(var(--rotate)) translateY(100px) scale(0);
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
          100% {
            transform: rotate(var(--rotate)) translateY(0) scale(var(--scale));
            opacity: 1;
          }
        }
        @keyframes crystalMerge {
          0% {
            transform: rotate(var(--rotate)) translateY(0) scale(var(--scale));
          }
          100% {
            transform: rotate(0) translateY(0) scale(0);
            opacity: 0;
          }
        }
        @keyframes shimmer {
          0%, 100% {
            filter: brightness(1) drop-shadow(0 0 10px rgba(255, 241, 152, 0.5));
          }
          50% {
            filter: brightness(1.2) drop-shadow(0 0 20px rgba(255, 241, 152, 0.8));
          }
        }
        .crystal-form {
          animation: crystalForm 0.5s ease-out forwards;
        }
        .crystal-merge {
          animation: crystalMerge 0.4s ease-in forwards;
        }
        .shimmer {
          animation: shimmer 1.5s ease-in-out infinite;
        }
      `}</style>

      <div className="flex flex-col items-center justify-center min-h-[50vh] sm:min-h-[60vh]">
        <div className="relative w-48 h-48">
          {/* Cristaux qui se forment */}
          {crystalPhase >= 1 && crystalPhase < 5 && crystals.map((crystal, i) => (
            <div
              key={i}
              className={`absolute left-1/2 top-1/2 -ml-4 -mt-8 ${crystalPhase >= 4 ? 'crystal-merge' : 'crystal-form'}`}
              style={{
                '--rotate': `${crystal.rotate}deg`,
                '--scale': crystal.scale,
                animationDelay: `${crystal.delay}s`
              } as React.CSSProperties}
            >
              {/* Forme de cristal */}
              <div
                className="w-8 h-16 bg-gradient-to-b from-tessan-yellow to-tessan-cream"
                style={{
                  clipPath: 'polygon(50% 0%, 100% 30%, 100% 70%, 50% 100%, 0% 70%, 0% 30%)'
                }}
              />
            </div>
          ))}

          {/* Badge formé */}
          {showBadge && (
            <div className="absolute inset-0 flex items-center justify-center shimmer">
              <div className="w-32 h-32 sm:w-44 sm:h-44 bg-tessan-cream rounded-2xl flex items-center justify-center shadow-2xl shadow-tessan-yellow/50">
                <span className="text-6xl sm:text-8xl">{badge.emoji}</span>
              </div>
            </div>
          )}

          {/* Centre lumineux pendant la formation */}
          {crystalPhase >= 2 && crystalPhase < 5 && (
            <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <div className="w-8 h-8 rounded-full bg-tessan-yellow animate-ping" />
            </div>
          )}
        </div>

        <div className={`mt-8 text-center transition-all duration-700 ${showBadge ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="text-2xl sm:text-4xl font-black text-tessan-yellow">{badge.name}</div>
          <div className="text-lg sm:text-xl text-tessan-cream mt-2">+{badge.points_awarded} points</div>
          <div className="text-tessan-cream/70 mt-4 max-w-[280px] sm:max-w-xs">{badge.description}</div>
        </div>
      </div>
    </AnimationLayout>
  )
}
